import { curryN, find, map, range, reverse, sortBy } from 'ramda'

const BLOCK_SIZE = 8

export const isPinned = (pinned, post) => {
  return pinned
    ? pinned.some((widget) => widget.posts && widget.posts.some((p) => p.id === post.id))
    : false
}

export const isFeatured = curryN(2, (featured, post) => {
  return featured ? featured.some((x) => x.id === post.id) : false
})

export const withActivities = curryN(2, (activities, items) => {
  // Admins should see a placeholder tile for where
  // the activity widget is normally rendered.
  if (!activities) {
    return [
      {
        type: 'activities',
        activities: 'EMPTY',
      },
      ...items,
    ]
  }

  const modified = items.slice()

  modified.splice(0, 0, {
    type: 'activities',
    activities,
  })

  return modified
})

export const withPinnedPosts = curryN(2, (pinned, items) => {
  const modified = items.slice()

  // Make sure we sort from smallest offset to largest so that ordering is kept.
  pinned
    .sort((a, b) => {
      return a.offset - b.offset
    })
    .forEach((widget) => {
      const onlyPost = widget.posts[0]
      if (onlyPost.status !== 'hidden') {
        modified.splice(widget.offset, 0, onlyPost)
      }
    })

  return modified
})

export const withFeaturedPosts = curryN(3, (featured, screenSize, items) => {
  const modified = items.slice()

  // Keeping nth-child ordering correct.
  if (featured.length) {
    // Only show featured carousel for large screen
    if (screenSize === 'large') {
      Array.prototype.splice.apply(modified, [0, 0].concat(createFeatured(featured)))
      // A Feature block takes the space of a two-up, so needs to create a placeholder
      Array.prototype.splice.apply(modified, [1, 0].concat(createPlaceholders(1)))
    } else {
      // Prepend posts for medium or small screen
      reverse(featured).forEach((post) => {
        modified.splice(0, 0, post)
      })
    }
  }

  return modified
})

export const withAds = curryN(4, (featured, fullAds, inlineAds, items) => {
  const modified = items.slice()

  // Interleave Ads
  const adPositionWithinBlock = 4 // Block Size is 2up + 6up = 8. we Want our ad in the 4th position
  const getInlineAdForIdx = getAdForIdx(
    inlineAds,
    (offset) => offset * BLOCK_SIZE + adPositionWithinBlock
  )

  // Count block length and add placeholders to pad out the block
  // This is helpful for ad positioning when blocks aren't full
  const blockLength = modified.length
  if (blockLength % 8 > 0) {
    const remainder = 8 - (blockLength % 8)
    Array.prototype.splice.apply(
      modified,
      [modified.length, 0].concat(createPlaceholders(remainder))
    )
  }

  for (let idx = adPositionWithinBlock, len = modified.length; idx < len; idx += BLOCK_SIZE) {
    let ad = getInlineAdForIdx(idx)

    if (ad) {
      if (idx === adPositionWithinBlock && !featured.length) {
        // XXX Hack for positioning an imediately positioned inline add into the first row
        // This makes the first block a 6-up so that we can nicely position the ad
        Array.prototype.splice.apply(modified, [0, 0].concat(createPlaceholders(2)))
      }

      modified.splice(idx, 0, { type: 'inline-ad', ad })

      // Increase used count of the ad for round robin
      ad.$usedCount = (ad.$usedCount || 0) + 1

      // Increase total length to traverse
      len += 1
    }
  }

  const getFullAdForIdx = getAdForIdx(fullAds, (offset) => offset * BLOCK_SIZE)
  let adOffset = 0
  for (let idx = 0, len = modified.length; idx < len; idx += BLOCK_SIZE) {
    let ad = getFullAdForIdx(idx)

    if (ad) {
      modified.splice(idx + adOffset, 0, { type: 'full-ad', ad })

      // Increase used count of the ad for round robin
      ad.$usedCount = (ad.$usedCount || 0) + 1

      // We need to adjust the splice point for full ads based on how many ads came before.
      adOffset++
    }
  }

  return modified
})

export const matchFrequencyOffset = (index, frequency, postOffset) => {
  if (frequency === 0) {
    // We use frequency 0 to specify non repeating offsets
    return index === postOffset
  } else {
    return index >= postOffset && (index - postOffset) % (frequency * BLOCK_SIZE) === 0
  }
}

const getAdForIdx = curryN(3, (ads, offsetFn, idx) => {
  return find(
    (ad) => {
      // The offset is based on a block. Our new block length is 8 posts long
      // a (2up + 6-up). We will need to calculate a post offset based on this.
      // Additionally we only want to place inline ads in the 4th position (0 indexed)
      let postOffset = offsetFn(ad.offset)
      return matchFrequencyOffset(idx, ad.frequency, postOffset)
    },
    sortBy((ad) => (ad.frequency === 0 ? -1 : ad.$usedCount || 0), ads)
  )
})

const createPlaceholders = (length) => map(() => ({ type: 'placeholder' }), range(0, length))

const createFeatured = (posts) => [{ type: 'featured', posts }]
